import { useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '@core/store'
import { rawIfc } from '@viewer/lib/rawIfc'
import { viewerApi } from '@viewer/lib/viewerApi'
import { conteudoEspacial, referenciasDoModelo } from './referenciasEmBruto'

// IFC entities that aggregate other entities. We split into two
// kinds because the relation chain differs:
//
//   GROUP-LIKE: members reached via IfcRelAssignsToGroup
//               (or IfcRelAggregates / IfcRelNests fallback)
//     IfcSystem + subtypes, IfcGroup, IfcZone, IfcElementAssembly,
//     IfcStructuralAnalysisModel
//
//   SPATIAL: contained elements reached via
//            IfcRelContainedInSpatialStructure
//     IfcSpace, IfcSpatialZone (used for fire compartments etc.)
//
// IfcGrid / IfcGridAxis are listed for visibility but have no
// "members" concept: clicking just selects the grid itself.
const GROUP_SECTIONS: Array<{ ifcType: string; label: string }> = [
  { ifcType: 'IFCSYSTEM', label: 'IfcSystem' },
  { ifcType: 'IFCDISTRIBUTIONSYSTEM', label: 'IfcDistributionSystem' },
  { ifcType: 'IFCBUILDINGSYSTEM', label: 'IfcBuildingSystem' },
  { ifcType: 'IFCSTRUCTURALANALYSISMODEL', label: 'IfcStructuralAnalysisModel' },
  { ifcType: 'IFCGROUP', label: 'IfcGroup' },
  // A IfcZone é um grupo: os espaços entram por IfcRelAssignsToGroup, e
  // nunca por contenção (como secção espacial mostrava sempre 0).
  { ifcType: 'IFCZONE', label: 'IfcZone' },
  { ifcType: 'IFCELEMENTASSEMBLY', label: 'IfcElementAssembly' },
]

const SPATIAL_SECTIONS: Array<{ ifcType: string; label: string }> = [
  { ifcType: 'IFCSPACE', label: 'IfcSpace' },
  { ifcType: 'IFCSPATIALZONE', label: 'IfcSpatialZone' },
]

const STANDALONE_SECTIONS: Array<{ ifcType: string; label: string }> = [
  { ifcType: 'IFCGRID', label: 'IfcGrid' },
]

interface Entity {
  expressID: number
  name: string | null
  memberCount: number
}

interface Section {
  ifcType: string
  label: string
  // 'group'    → membros via IfcRelAssignsToGroup (count from rawIfc)
  // 'spatial'  → elementos contidos (IfcRelContainedInSpatialStructure) ou
  //               referenciados (IfcRelReferencedInSpatialStructure)
  // 'standalone' → entidade pura (IfcGrid), sem membros enumerados
  kind: 'group' | 'spatial' | 'standalone'
  entities: Entity[]
}

/** Uma só lista vazia, partilhada: devolver `[]` novo a cada render
 *  fazia os memos a jusante correrem sempre. */
const EMPTY_SECTIONS: Section[] = []

function LogicalBrowser() {
  const activeModelId = useAppStore((s) => s.activeModelId)
  const selectedTargets = useAppStore((s) => s.selectedTargets)
  // As secções guardam consigo o modelo de que vieram, e ficam velhas
  // sozinhas quando ele muda. Limpá-las no corpo do efeito encadeava
  // renders, que era o que o lint apontava.
  const [carga, setCarga] = useState<{
    modelId: string | null
    sections: Section[]
  }>({ modelId: null, sections: [] })
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(['IFCSYSTEM', 'IFCDISTRIBUTIONSYSTEM', 'IFCSPACE']),
  )
  // Per-section anchor for shift-range selection.
  const lastClickRef = useRef<{ ifcType: string; index: number } | null>(null)

  // Subscribe to selection so we can highlight active groups.
  const selectedIdSet = useMemo(() => {
    const s = new Set<number>()
    if (!activeModelId) return s
    for (const t of selectedTargets) {
      if (t.modelId === activeModelId) s.add(t.localId)
    }
    return s
  }, [selectedTargets, activeModelId])

  const actual = carga.modelId === activeModelId
  const sections = actual ? carga.sections : EMPTY_SECTIONS
  const loading = Boolean(activeModelId) && !actual

  useEffect(() => {
    if (!activeModelId) return
    const tryLoad = () => {
      if (!rawIfc.hasModel(activeModelId)) return false
      const groupCounts = rawIfc.getGroupMemberCounts(activeModelId)
      const containmentCounts = rawIfc.getContainmentCounts(activeModelId)
      const out: Section[] = []
      const buildSection = (
        spec: { ifcType: string; label: string },
        kind: 'group' | 'spatial' | 'standalone',
      ): Section | null => {
        const ids = rawIfc.getLineIDsByType(activeModelId, spec.ifcType)
        if (ids.length === 0) return null
        const entities: Entity[] = ids.map((id) => {
          const attrs = rawIfc.getAttributes(activeModelId, id)
          let count = 0
          if (kind === 'group') count = groupCounts.get(id) ?? 0
          else if (kind === 'spatial') {
            // Contidos e referenciados: uma IfcSpatialZone referencia os
            // elementos, não os contém.
            const refs = referenciasDoModelo(activeModelId).get(id)
            count = refs ? conteudoEspacial(activeModelId, id).length : containmentCounts.get(id) ?? 0
          }
          return {
            expressID: id,
            name: attrs?.name ?? null,
            memberCount: count,
          }
        })
        return { ifcType: spec.ifcType, label: spec.label, kind, entities }
      }
      for (const sec of GROUP_SECTIONS) {
        const s = buildSection(sec, 'group')
        if (s) out.push(s)
      }
      for (const sec of SPATIAL_SECTIONS) {
        const s = buildSection(sec, 'spatial')
        if (s) out.push(s)
      }
      for (const sec of STANDALONE_SECTIONS) {
        const s = buildSection(sec, 'standalone')
        if (s) out.push(s)
      }
      setCarga({ modelId: activeModelId, sections: out })
      return true
    }
    if (tryLoad()) return
    const unsub = rawIfc.subscribe(() => {
      if (tryLoad()) unsub()
    })
    return unsub
  }, [activeModelId])

  const toggle = (ifcType: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(ifcType)) next.delete(ifcType)
      else next.add(ifcType)
      return next
    })
  }

  const buildSelectionRange = (
    section: Section,
    index: number,
    ev: React.MouseEvent,
  ): { ids: number[]; mode: 'replace' | 'add' } => {
    const cur = section.entities[index]
    if (!cur) return { ids: [], mode: 'replace' }
    if (
      ev.shiftKey &&
      lastClickRef.current?.ifcType === section.ifcType
    ) {
      const a = Math.min(lastClickRef.current.index, index)
      const b = Math.max(lastClickRef.current.index, index)
      const range = section.entities.slice(a, b + 1).map((e) => e.expressID)
      return { ids: range, mode: 'add' }
    }
    lastClickRef.current = { ifcType: section.ifcType, index }
    if (ev.ctrlKey || ev.metaKey) return { ids: [cur.expressID], mode: 'add' }
    return { ids: [cur.expressID], mode: 'replace' }
  }

  const resolveContents = (kind: Section['kind'], parentIds: number[]): number[] => {
    if (!activeModelId) return []
    const all: number[] = []
    for (const pid of parentIds) {
      const ids =
        kind === 'group'
          ? rawIfc.getGroupMembers(activeModelId, pid)
          : kind === 'spatial'
            ? conteudoEspacial(activeModelId, pid)
            : []
      all.push(...ids)
    }
    return all
  }

  // Click on the entity name → select the entity itself (the
  // IfcSystem / IfcSpace / ...). The user can then inspect its
  // properties in the right sidebar without losing the entity
  // identity.
  const selectEntities = (ids: number[], mode: 'replace' | 'add') => {
    if (!activeModelId) return
    const targets = ids.map((id) => ({ modelId: activeModelId, localId: id }))
    useAppStore.getState().applyBulkSelection(targets, mode)
  }

  // Click on the count badge → select the contained / member
  // elements instead of the entity itself.
  const selectContents = (
    kind: Section['kind'],
    parentIds: number[],
    mode: 'replace' | 'add',
  ) => {
    const ids = resolveContents(kind, parentIds)
    if (ids.length === 0) {
      const verb =
        kind === 'group'
          ? 'IfcRelAssignsToGroup / IfcRelAggregates / IfcRelNests'
          : 'IfcRelContainedInSpatialStructure'
      window.alert(
        `Sem conteúdo associado via ${verb} neste IFC para a(s) entidade(s) seleccionada(s).`,
      )
      return
    }
    selectEntities(ids, mode)
  }

  const hideContents = (kind: Section['kind'], parentId: number) => {
    const ids = resolveContents(kind, [parentId])
    if (ids.length === 0) return
    selectEntities(ids, 'replace')
    void viewerApi.hideSelected()
  }

  const isolateContents = (kind: Section['kind'], parentId: number) => {
    const ids = resolveContents(kind, [parentId])
    if (ids.length === 0) return
    selectEntities(ids, 'replace')
    void viewerApi.isolateSelected()
  }

  if (!activeModelId) {
    return (
      <div className="p-3 text-[11px] italic text-[var(--color-text-muted)]">
        Carrega um IFC para ver as suas agregações lógicas.
      </div>
    )
  }
  if (loading) {
    return (
      <div className="p-3 text-[11px] italic text-[var(--color-text-muted)]">
        A ler IFC paralelo (web-ifc)…
      </div>
    )
  }
  if (sections.length === 0) {
    return (
      <div className="p-3 text-[11px] italic text-[var(--color-text-muted)]">
        Este IFC não declara IfcSystem, IfcGroup, IfcZone, IfcElementAssembly,
        IfcGrid nem IfcSpatialZone.
      </div>
    )
  }

  // Bulk action: when multiple group/spatial entities are selected,
  // offer a single click to swap the selection for the union of
  // their contents. Without this the user would have to click "N →"
  // on each entry one by one.
  const selectedEntityCount = (() => {
    let n = 0
    for (const sec of sections) {
      if (sec.kind === 'standalone') continue
      for (const e of sec.entities) {
        if (selectedIdSet.has(e.expressID)) n++
      }
    }
    return n
  })()

  const resolveBulkContents = (): number[] => {
    if (!activeModelId) return []
    const all: number[] = []
    for (const sec of sections) {
      if (sec.kind === 'standalone') continue
      for (const e of sec.entities) {
        if (!selectedIdSet.has(e.expressID)) continue
        const ids =
          sec.kind === 'group'
            ? rawIfc.getGroupMembers(activeModelId, e.expressID)
            : conteudoEspacial(activeModelId, e.expressID)
        all.push(...ids)
      }
    }
    return all
  }

  return (
    <div className="space-y-1 p-2 text-[11px]">
      {selectedEntityCount > 1 && (
        <div className="sticky top-0 z-10 mb-1 flex items-center justify-between rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-1">
          <span className="text-[10px] text-[var(--color-accent)]">
            {selectedEntityCount} entidades seleccionadas
          </span>
          <button
            type="button"
            onClick={() => {
              const ids = resolveBulkContents()
              if (ids.length === 0) {
                window.alert(
                  'Nenhuma das entidades seleccionadas tem conteúdo via IfcRelAssignsToGroup, IfcRelContainedInSpatialStructure ou IfcRelReferencedInSpatialStructure.',
                )
                return
              }
              if (!activeModelId) return
              const targets = ids.map((id) => ({
                modelId: activeModelId,
                localId: id,
              }))
              useAppStore.getState().applyBulkSelection(targets, 'replace')
            }}
            className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-0.5 text-[10px] text-[var(--color-accent)] hover:bg-[var(--color-accent)]/20"
            title="Substituir a selecção pela união dos elementos contidos / membros das entidades actualmente seleccionadas"
          >
            Selecionar conteúdo de todos →
          </button>
        </div>
      )}
      {sections.map((sec) => {
        const open = expanded.has(sec.ifcType)
        const contentLabel =
          sec.kind === 'spatial' ? 'elementos contidos ou referenciados' : 'membros'
        // Per-section content count and "select all content" action.
        // Skipped for standalone sections (IfcGrid has no content).
        const sectionContentCount =
          sec.kind === 'standalone'
            ? 0
            : sec.entities.reduce((acc, e) => acc + e.memberCount, 0)
        return (
          <div key={sec.ifcType}>
            <div className="grid w-full grid-cols-[1fr_auto_auto_auto] items-center gap-x-2 rounded px-1 py-0.5 hover:bg-[var(--color-bg)]">
              <button
                type="button"
                onClick={() => toggle(sec.ifcType)}
                className="truncate text-left font-mono text-[10px] text-[var(--color-text)]"
              >
                {sec.label}
              </button>
              <span className="text-right text-[10px] text-[var(--color-text-muted)]">
                {sec.entities.length}
              </span>
              {sec.kind !== 'standalone' && (
                <button
                  type="button"
                  onClick={(ev) => {
                    if (sec.entities.length === 0) return
                    selectContents(
                      sec.kind,
                      sec.entities.map((e) => e.expressID),
                      ev.ctrlKey || ev.metaKey ? 'add' : 'replace',
                    )
                  }}
                  disabled={sectionContentCount === 0}
                  className="rounded px-1 text-[10px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)] disabled:opacity-30"
                  title={
                    sectionContentCount > 0
                      ? `Selecionar conteúdo de TODOS os ${sec.label} (${sectionContentCount} ${contentLabel})`
                      : `Sem ${contentLabel} associados em ${sec.label}`
                  }
                >
                  {sectionContentCount} →
                </button>
              )}
              {sec.kind === 'standalone' && (
                <span className="px-1 text-[10px] text-[var(--color-text-dim)]">-</span>
              )}
              <button
                type="button"
                onClick={() => toggle(sec.ifcType)}
                className="text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
              >
                {open ? '−' : '+'}
              </button>
            </div>
            {open && (
              <ul className="ml-2 mt-0.5 flex flex-col gap-px border-l border-[var(--color-border)]/40 pl-2">
                {sec.entities.map((e, idx) => {
                  const isStandalone = sec.kind === 'standalone'
                  const hasContent = e.memberCount > 0 || isStandalone
                  // Highlight: only the entity itself being in the
                  // selection now lights the row (cleaner than the
                  // "partially active because some members overlap"
                  // heuristic that the user found confusing).
                  const entityActive = selectedIdSet.has(e.expressID)
                  const rowClass = entityActive
                    ? 'border-l-2 border-[var(--color-accent)] bg-[var(--color-accent-bg)]'
                    : 'border-l-2 border-transparent'
                  return (
                    <li key={e.expressID} className="group">
                      <div
                        className={`grid w-full grid-cols-[1fr_auto_auto] items-baseline gap-x-2 rounded px-1 py-0.5 hover:bg-[var(--color-bg)] ${rowClass}`}
                      >
                        <button
                          type="button"
                          onClick={(ev) => {
                            const { ids, mode } = buildSelectionRange(
                              sec,
                              idx,
                              ev,
                            )
                            selectEntities(ids, mode)
                          }}
                          className="truncate text-left text-[10px] text-[var(--color-text)]"
                          title={`Selecionar a entidade ${sec.label} #${e.expressID}\nShift: range desde a última escolha\nCtrl/Cmd: somar`}
                        >
                          {e.name ?? '(sem nome)'}
                        </button>
                        {!isStandalone && (
                          <button
                            type="button"
                            onClick={(ev) => {
                              if (!hasContent) return
                              selectContents(
                                sec.kind,
                                [e.expressID],
                                ev.ctrlKey || ev.metaKey ? 'add' : 'replace',
                              )
                            }}
                            disabled={!hasContent}
                            className="rounded px-1 text-[10px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)] disabled:opacity-40"
                            title={
                              hasContent
                                ? `Selecionar ${e.memberCount} ${contentLabel} (Ctrl/Cmd: somar)`
                                : `Sem ${contentLabel} associados`
                            }
                          >
                            {e.memberCount} →
                          </button>
                        )}
                        {isStandalone && (
                          <span className="px-1 text-[10px] text-[var(--color-text-dim)]">-</span>
                        )}
                        <span className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            disabled={!hasContent}
                            onClick={() => hideContents(sec.kind, e.expressID)}
                            title={`Esconder ${contentLabel} desta entidade`}
                            className="rounded px-1 text-[9px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
                          >
                            esconder
                          </button>
                          <button
                            type="button"
                            disabled={!hasContent}
                            onClick={() => isolateContents(sec.kind, e.expressID)}
                            title="Isolar (esconder tudo o resto e mostrar só estes)"
                            className="rounded px-1 text-[9px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-30"
                          >
                            isolar
                          </button>
                        </span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default LogicalBrowser
